import { ErrorMessage } from "components/shared/ErrorMessage";
import { SpinnerButton } from "components/shared/SpinnerButton";
import { accountMessages } from "domain/account/accountMessages";
import { changeEmail } from "domain/account/changeEmail";
import { redirectActions } from "domain/redirect/redirectActions";
import { userActions } from "domain/user/userActions";
import { changeEmailMutationResponse } from "generatedQueries/changeEmailMutation.graphql";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useRelayEnvironment } from "react-relay";
import { Form, Label } from "reactstrap";
import { PayloadError } from "relay-runtime";
import { userRoutes } from "routes/user/routes";
import { Message } from "shared/components/Message";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { sharedMessages } from "shared/sharedMessages";
import { combine, isEmail, required } from "shared/validations/validations";

type ChangeEmailFormValues = {
    email: string | null;
};

export const ChangeEmail: FC = () => {
    const { formatMessage } = useIntl();
    const environment = useRelayEnvironment();
    const dispatch = useDispatch();

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const useFormReturn = useForm<ChangeEmailFormValues>({ mode: "onBlur" });
    const { control, handleSubmit, register } = useFormReturn;

    if (error) {
        return <ErrorMessage heading={sharedMessages.requestFailedTitle} message={sharedMessages.requestFailedBody} />;
    }

    return <FormProvider {...useFormReturn}>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate={true} role="form">
            <div className="form-group row">
                <Label className="col-form-label col-sm-2" for="email">
                    <Message message={accountMessages.newEmailAddressLabel} />
                </Label>
                <div className="col-sm-4">
                    <TextBox
                        id="email"
                        name="email"
                        control={control}
                        register={register}
                        type="email"
                        autoComplete="email"
                        placeholder={formatMessage(accountMessages.newEmailAddressLabel)}
                        options={combine<ChangeEmailFormValues, "email">(required(), isEmail())}
                        aria-required={true}
                    />
                </div>
            </div>
            <SpinnerButton
                color="primary"
                message={accountMessages.changeEmailAddressButtonLabel}
                isLoading={submitting}
                type="submit"
            />
        </Form>
    </FormProvider>;

    function onSubmit({ email }: ChangeEmailFormValues) {
        if (!email) {
            return;
        }

        setSubmitting(true);
        changeEmail(environment, email, onChangeEmailResponse, onChangeEmailError);
    }

    function onChangeEmailResponse(_: changeEmailMutationResponse, errors?: PayloadError[] | null) {
        if (errors && errors.length > 0) {
            setError(true);
            setSubmitting(false);
        } else {
            dispatch(userActions.logout());
            dispatch(redirectActions.redirectTo(userRoutes.emailChanged.create({})));
        }
    }

    function onChangeEmailError() {
        setError(true);
        setSubmitting(false);
    }
};
